import { useState } from 'react';
import { GraphQLClient } from 'graphql-request';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { getToken } from './services/auth/index.js';
import './utils/logToSlack.js';
import { fetchPolicyLookup } from './utils/fetchPolicyLookup.js';
import { getCookies, setCookie } from './utils/cookies.js';
import './utils/currency.js';
import './context/index.js';
import { b as COOKIE_CURRENCY_NAME, a as COOKIE_LANGUAGE_NAME, l as COOKIE_PROMO_TAG } from './cookies-l-jEMp6P.js';
import './currency-_kacFJHO.js';
import { a as LanguageCode } from './language-BHHwgtQp.js';
import './payment-YC9H491Y.js';
import './product-_pRduMz7.js';
import 'next/router';
import './helpers-kA_hdoOI.js';
import 'lodash';

/* eslint-disable @typescript-eslint/no-explicit-any */
const graphqlRequestClient = (uri) => {
    const client = new GraphQLClient(uri);
    return client;
};
const getHeaders = (Store, options = {}, ctx, isPrivate = false, fetchPolicyKey) => {
    var _a;
    const cookies = getCookies(ctx);
    const currency = (ctx === null || ctx === void 0 ? void 0 : ctx.currency) || cookies[COOKIE_CURRENCY_NAME] || '';
    const cookieLocale = cookies[COOKIE_LANGUAGE_NAME] === 'default' ? 'en' : cookies[COOKIE_LANGUAGE_NAME];
    // Get "locale" param from a cookie
    const locale = ((ctx === null || ctx === void 0 ? void 0 : ctx.locale) === 'default' ? 'en' : ctx === null || ctx === void 0 ? void 0 : ctx.locale) ||
        cookieLocale ||
        LanguageCode.En;
    // Get "store" param based on "locale"
    const store = Store[locale] || Store.default;
    const promoTag = ((_a = ctx === null || ctx === void 0 ? void 0 : ctx.query) === null || _a === void 0 ? void 0 : _a['promo-tag']) || cookies[COOKIE_PROMO_TAG] || null;
    if (!cookies[COOKIE_PROMO_TAG] && promoTag) {
        setCookie(COOKIE_PROMO_TAG, promoTag, ctx);
    }
    if (!cookies[COOKIE_CURRENCY_NAME] && currency) {
        setCookie(COOKIE_CURRENCY_NAME, currency, ctx);
    }
    const authToken = getToken(ctx);
    const optionsCopy = cloneDeep(options);
    const currentHeaders = get(optionsCopy, ['context', 'headers'], {});
    const fetchPolicy = fetchPolicyKey ? fetchPolicyLookup(fetchPolicyKey) : 'no-cache';
    // Extract operation name and variables
    let operationName = fetchPolicyKey || '';
    const variables = JSON.stringify((optionsCopy === null || optionsCopy === void 0 ? void 0 : optionsCopy.variables) || {});
    // Check if the operation name is "GetPromotion" and add the current date in Paris time zone
    if (operationName === 'GetPromotion') {
        const parisTimeZone = 'Europe/Paris';
        const currentDate = new Intl.DateTimeFormat('en-GB', {
            timeZone: parisTimeZone,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(new Date());
        operationName = `${operationName} - ${currentDate}`;
    }
    if (operationName === 'GetCurrencies') {
        operationName = `${operationName} - ${locale}`;
    }
    if (operationName === 'GetYmal') {
        operationName = `${operationName} - ${locale} - ${currency}`;
    }
    const isCacheDisabled = process.env.NEXT_PUBLIC_CACHE_DISABLED === 'true';
    // Be careful not to add any headers with a potentially null value
    const headers = {
        ...currentHeaders,
        Store: store,
        'Content-Currency': currency,
        ...(promoTag !== null && !isCacheDisabled ? { 'promo-tag': promoTag } : {}),
        ...(operationName !== null && fetchPolicy !== 'no-cache' && !isCacheDisabled
            ? { CacheKeyOperation: operationName }
            : {}),
        ...(variables !== null && fetchPolicy !== 'no-cache' && !isCacheDisabled
            ? { CacheKeyVariables: variables }
            : {}),
    };
    if (isPrivate) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    return headers;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useQuery = (uri, query, apolloProps, isPrivate, fetchPolicyKey) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const func = options => {
        setError(null);
        setLoading(true);
        const headers = getHeaders(apolloProps.store, options, null, isPrivate, fetchPolicyKey);
        return graphqlRequestClient(uri)
            .request(query, options === null || options === void 0 ? void 0 : options.variables, headers)
            .then(data => ({ data, error: undefined }))
            .catch((e) => {
            setError(e);
            return { data: undefined, error: e };
        })
            .finally(() => {
            setLoading(false);
        });
    };
    return [func, { loading, error }];
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useMutation = (uri, query, headers) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const func = options => {
        setError(null);
        setLoading(true);
        return graphqlRequestClient(uri)
            .request(query, options === null || options === void 0 ? void 0 : options.variables, headers)
            .then(data => ({ data, error: undefined }))
            .catch((e) => {
            setError(e);
            return { data: undefined, error: e };
        })
            .finally(() => {
            setLoading(false);
        });
    };
    return [func, { loading, error }];
};

const publicQuery = (apolloProps, options, fetchPolicyKey, ctx, useGetForQueries = false) => {
    const newOptions = {
        ...options,
        context: {
            fetchOptions: { method: useGetForQueries ? 'GET' : 'POST' },
        },
    };
    return graphqlRequestClient(apolloProps.uri)
        .request(options.query, options.variables, getHeaders(apolloProps.store, newOptions, ctx, false, fetchPolicyKey))
        .then(data => ({ data, errors: [] }))
        .catch(error => ({ data: null, errors: error.response.errors }));
};
const privateQuery = (apolloProps, options, fetchPolicyKey, ctx) => graphqlRequestClient(apolloProps.uri)
    .request(options.query, options.variables, getHeaders(apolloProps.store, options, ctx, true, fetchPolicyKey))
    .then(data => ({ data, errors: [] }))
    .catch(error => ({ data: null, errors: error.response.errors }));
const publicMutate = (apolloProps, options, ctx) => graphqlRequestClient(apolloProps.uri)
    .request(options.mutation, options.variables, getHeaders(apolloProps.store, options, ctx, true))
    .then(data => ({ data, errors: [] }))
    .catch(error => ({ data: null, errors: error.response.errors }));
const privateMutate = (apolloProps, options, ctx) => graphqlRequestClient(apolloProps.uri)
    .request(options.mutation, options.variables, getHeaders(apolloProps.store, options, ctx, true))
    .then(data => ({ data, errors: [] }))
    .catch(error => ({ data: null, errors: error.response.errors }));
const usePublicLazyQuery = (apolloProps, query, fetchPolicyKey) => useQuery(apolloProps.uri, query, apolloProps, false, fetchPolicyKey);
const usePrivateLazyQuery = (apolloProps, query, fetchPolicyKey) => useQuery(apolloProps.uri, query, apolloProps, true, fetchPolicyKey);
const usePublicMutation = (apolloProps, mutation, options) => useMutation(apolloProps.uri, mutation, getHeaders(apolloProps.store, options));
const usePrivateMutation = (apolloProps, mutation, options) => useMutation(apolloProps.uri, mutation, getHeaders(apolloProps.store, options, null, true));

export { privateQuery as a, publicMutate as b, privateMutate as c, usePrivateLazyQuery as d, usePublicMutation as e, usePrivateMutation as f, publicQuery as p, usePublicLazyQuery as u };
//# sourceMappingURL=requestsCollection-gritD79O.js.map
